import { Card } from 'components/Card';
import { CardRow } from 'components/CardRow';
import { TextAreaField } from 'components/Field/TextAreaField/TextAreaField';
import { ICONS } from 'components/Icon';
import { ModalScreenProps, ModalScreens } from 'components/Navigation/types';
import { Page } from 'components/Page';
import { useAlert } from 'hooks/useAlert';
import { useConfirmUnsaved } from 'hooks/useConfirm';
import { useForceUpdate } from 'hooks/useForceUpdate';
import { useEsc } from 'hooks/useEsc';
import { useSelector } from 'hooks/useSelector';
import { cloneDeep, isEqual, sortBy } from 'lodash';
import React from 'react';
import { useDispatch } from 'react-redux';
import {
  NAVIGATE_MODAL,
  NAVIGATE_MODAL_BACK,
  REMOTE_ASESETUP_CONTROLCLOSED,
  REMOTE_ASESETUP_CONTROLSAVE,
  SET_INCOMING_VALUE,
} from 'store/actions';
import { selectIncomingValue } from 'store/app/selectors';
import {
  selectAseSetupControlId,
  selectRemoteAseSetupControl,
  selectRemoteCategories,
  selectRemoteCategoryGroups,
} from 'store/remote/selectors';
import { AssessmentSuggestions } from 'store/remote/types';
import { PageHeader } from 'components/PageHeader';

export const ControlScreen: React.FC<
  ModalScreenProps<ModalScreens.ASESETUPLIBRARYCONTROL>
> = ({ route, navigation }) => {
  const dispatch = useDispatch();
  const alert = useAlert();
  const confirmUnsaved = useConfirmUnsaved();
  const forceUpdate = useForceUpdate();

  const { incomingValueNewIdKey } = route.params;

  const incomingValueKeyField = 'AseSetupControlField';
  const incomingValueField = useSelector((state) =>
    selectIncomingValue(state, incomingValueKeyField),
  ) as Record<string, number> | undefined;

  const controlId = useSelector(selectAseSetupControlId);

  const control: AssessmentSuggestions['controls'][number] = useSelector(
    (state) => selectRemoteAseSetupControl(state, controlId || -1),
  ) || {
    id: 0,
    what: '',
    why: '',
    categoryId: 0,
    negativeChecklistItemIds: [],
    positiveChecklistItemIds: [],
    questionIds: [],
  };

  const categories = useSelector(selectRemoteCategories)!;
  const categoryGroups = useSelector(selectRemoteCategoryGroups)!;

  const isDirty = React.useRef(false);
  const data = React.useRef(cloneDeep(control));

  // Reset data if ID changes, then check isDirty
  if (data.current.id !== control.id) {
    data.current = cloneDeep(control);
  }
  isDirty.current = !isEqual(control, data.current);

  const category = categories[data.current.categoryId] ?? categories[0]!;

  const shouldReload = () => {
    if (isDirty.current !== !isEqual(control, data.current)) {
      forceUpdate();
    }
  };

  const handleChangeWithName = <T extends keyof typeof data.current>(
    name: T,
    value: (typeof data.current)[T],
  ) => {
    data.current = {
      ...data.current,
      [name]: value,
    };
    shouldReload();
  };

  const handleClose = () => {
    if (isDirty.current) {
      confirmUnsaved({
        onSave: handleSave,
        onDiscard: () => dispatch(NAVIGATE_MODAL_BACK()),
      });
      return;
    }
    dispatch(NAVIGATE_MODAL_BACK());
  };

  const handleSave = () => {
    if (data.current.what.trim() === '') {
      alert({ text: 'Please complete "What is the control?"' });
      return;
    }
    dispatch(
      REMOTE_ASESETUP_CONTROLSAVE({
        id: controlId,
        data: data.current,
        incomingValueNewIdKey,
      }),
    );
  };

  const handleSavePress = () => {
    if (isDirty.current || !control.id) {
      handleSave();
    } else {
      dispatch(NAVIGATE_MODAL_BACK());
    }
  };

  const handleCategoryPress = () =>
    dispatch(
      NAVIGATE_MODAL({
        screen: ModalScreens.SELECT,
        params: {
          title: 'Category',
          fieldName: 'categoryId',
          incomingValueKey: incomingValueKeyField,
          returnType: 'number',
          value: data.current.categoryId,
          options: sortBy(
            Object.values(categories).map((cat) => ({
              name: cat.name,
              groupName: categoryGroups[cat.groupId]!.name,
              internalValue: cat.id,
              ordering:
                (cat.id === 0 ? 0 : categoryGroups[cat.groupId]!.name) +
                cat.name,
            })),
            ['ordering'],
          ),
        },
      }),
    );

  React.useEffect(() => {
    if (incomingValueField) {
      data.current = {
        ...data.current,
        ...incomingValueField,
      };
      dispatch(SET_INCOMING_VALUE({ key: incomingValueKeyField }));
    }
  }, [dispatch, incomingValueField]);

  React.useEffect(() => {
    return () => {
      dispatch(REMOTE_ASESETUP_CONTROLCLOSED());
    };
  }, [dispatch]);

  const isDirtyVar = isDirty.current;
  React.useEffect(() => {
    navigation?.setOptions({ gestureEnabled: !isDirtyVar });
  }, [navigation, isDirtyVar]);
  useEsc(handleClose);

  return (
    <Page
      scrollView
      header={
        <PageHeader
          title={controlId ? 'Control' : 'New Control'}
          onPressCancelButton={handleClose}
          onPressSaveButton={
            controlId || isDirty.current ? handleSavePress : null
          }
        />
      }
    >
      <Card label="What">
        <TextAreaField
          name="what"
          onChangeWithName={handleChangeWithName}
          placeholder="What is the control? (required)"
          value={data.current.what}
          onEsc={handleClose}
          autoFocusWeb
          autoFocusNative={!data.current.id}
          autoFocusDelay
          showFieldTypeIcon={false}
        />
      </Card>
      <Card label="Why">
        <TextAreaField
          name="why"
          onChangeWithName={handleChangeWithName}
          placeholder="Why is it needed?"
          value={data.current.why}
          onEsc={handleClose}
          showFieldTypeIcon={false}
        />
      </Card>
      <Card>
        {Object.keys(categories).length > 1 ? (
          <CardRow
            label="Category"
            value={category.name}
            onPress={handleCategoryPress}
            icon={ICONS.TagLight}
          />
        ) : (
          <CardRow
            label="Category"
            value="No categories set up"
            navigationIcon={ICONS.EmptySetLight}
            icon={ICONS.TagLight}
          />
        )}
      </Card>
    </Page>
  );
};
